import { ContentType, type MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

const BOT_SYSTEM = 'https://unify.health/bots';

function botTarget(value: string, envId?: string): string | Identifier {
  const id = envId?.trim();
  return id || { system: BOT_SYSTEM, value };
}

export type RequestableGroupProjection = {
  groupId: string;
  name: string;
  description?: string;
  imageUrl?: string;
  scheduleSummary?: string;
  timezone?: string;
  modality?: string;
  organizationId?: string;
  organizationName?: string;
  groupTemplateId?: string;
  enrollmentMode?: string;
  availability: 'accepting' | 'waitlist' | 'unavailable';
  seatsRemaining?: number;
};

/** Insurance the requester entered because it differs from the plans on file. */
export type CreateServiceRequestCoverage = {
  priority: 'primary' | 'secondary' | 'tertiary';
  payerOrgId: string;
  payerName: string;
  memberId: string;
  groupNumber?: string;
  relationship?: string;
  subscriberFirstName?: string;
  subscriberLastName?: string;
  subscriberDateOfBirth?: string;
  periodStart?: string;
  periodEnd?: string;
};

export type CreateServiceRequestCommand = {
  patientId: string;
  therapyTypeCode: string;
  requestedGroupId?: string;
  requestedGroupTemplateId?: string;
  reason?: string;
  paymentType?: 'insurance' | 'self-pay';
  coverage?: CreateServiceRequestCoverage;
  submissionId: string;
};

export type CreateServiceRequestResult = {
  operation: 'create-service-request';
  outcome: 'created' | 'duplicate' | 'idempotent-replay';
  serviceRequestId: string;
  episodeOfCareId: string;
  trackerStage: string;
  serviceRequestStatus: string;
  episodeOfCareStatus: string;
};

type ListRequestableGroupsResult = {
  operation: 'list-requestable-groups';
  groups: RequestableGroupProjection[];
  helpFindOption: { id: 'help-find'; label: string };
};

function target(): string | Identifier {
  return botTarget(
    'service-request-originator',
    import.meta.env.VITE_MEDPLUM_SERVICE_REQUEST_ORIGINATOR_BOT_ID,
  );
}

export async function listRequestableGroups(
  medplum: MedplumClient,
  organizationId?: string,
): Promise<ListRequestableGroupsResult> {
  return (await medplum.executeBot(
    target(),
    {
      operation: 'list-requestable-groups',
      ...(organizationId ? { organizationId } : {}),
    },
    ContentType.JSON,
  )) as ListRequestableGroupsResult;
}

export async function createServiceRequestViaOriginator(
  medplum: MedplumClient,
  command: CreateServiceRequestCommand,
): Promise<CreateServiceRequestResult> {
  return (await medplum.executeBot(
    target(),
    { operation: 'create-service-request', ...command },
    ContentType.JSON,
  )) as CreateServiceRequestResult;
}
